import type { ToastServiceMethods } from "primevue/toastservice";
import { useToast } from "primevue/usetoast";
import { reactive } from "vue";
import { useI18n } from "vue-i18n";

interface UndoToastOptions {
  summary: string;
  detail?: string;
  undoAction: () => void | Promise<void>;
  life?: number;
}

let nextUndoId = 0;

export const undoActions = reactive(new Map<number, () => Promise<void>>());

export function getUndoAction(
  message: Record<string, unknown>,
): (() => Promise<void>) | undefined {
  const id = message._undoId as number | undefined;
  return id != null ? undoActions.get(id) : undefined;
}

export function useUndoToast(): {
  showUndoToast: (options: UndoToastOptions) => { undo: () => Promise<void> };
  toast: ToastServiceMethods;
} {
  const toast = useToast();
  const { t } = useI18n();

  function showUndoToast(options: UndoToastOptions): {
    undo: () => Promise<void>;
  } {
    const id = nextUndoId++;
    const life = options.life ?? 5000;
    let undone = false;

    const undoFn = async (): Promise<void> => {
      if (undone) return;
      undone = true;
      undoActions.delete(id);
      await options.undoAction();
    };

    undoActions.set(id, undoFn);

    setTimeout(() => undoActions.delete(id), life + 500);

    toast.add({
      severity: "contrast",
      summary: options.summary,
      detail: options.detail ?? t("admin_common.undo_hint"),
      life,
      group: "admin-undo",
      _undoId: id,
    } as Record<string, unknown>);

    return { undo: undoFn };
  }

  return { showUndoToast, toast };
}
